import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, CheckCircle2, AlertCircle, Send, Clock, TrendingUp, ChevronRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AdminLayout from '../components/AdminLayout';
import { formatUkDateTime } from '../lib/date';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  quote_requested: { label: 'Quote Requested', tone: 'bg-amber-100 text-amber-800' },
  quote_sent: { label: 'Quote Sent', tone: 'bg-blue-100 text-blue-800' },
  quote_accepted: { label: 'Accepted', tone: 'bg-emerald-100 text-emerald-800' },
  confirmed: { label: 'Confirmed', tone: 'bg-emerald-100 text-emerald-800' },
  in_progress: { label: 'In Progress', tone: 'bg-indigo-100 text-indigo-800' },
  completed: { label: 'Completed', tone: 'bg-gray-100 text-gray-700' },
  cancelled: { label: 'Cancelled', tone: 'bg-red-100 text-red-700' },
  modification_pending: { label: 'Modification Pending', tone: 'bg-amber-100 text-amber-800' },
};

const toLocalISODate = (d) => {
  const offset = d.getTimezoneOffset();
  const dd = new Date(d.getTime() - offset * 60 * 1000);
  return dd.toISOString().split('T')[0];
};

const AdminOverview = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const resetData = async () => {
    if (!window.confirm('This permanently deletes ALL bookings, invoices and customers. Drivers and your login are kept. Continue?')) return;
    const typed = window.prompt('Type DELETE to confirm you want to erase all bookings, invoices and customers.');
    if (typed !== 'DELETE') { toast.error('Cancelled — you must type DELETE exactly.'); return; }
    setResetting(true);
    try {
      const { data } = await axios.post(`${API}/admin/reset-data`, { confirm: 'DELETE' });
      const d = data.deleted || {};
      toast.success(`Cleared ${d.bookings || 0} bookings, ${d.invoices || 0} invoices, ${d.customer_accounts || 0} customers`);
      setBookings([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not clear data');
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bookings?limit=500`);
        if (mounted) setBookings(data.bookings || []);
      } catch (e) {
        console.error('Failed to load bookings', e);
        toast.error('Could not load bookings');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const today = toLocalISODate(new Date());
  const todayBookings = bookings.filter((b) => b.pickupDate === today);
  const quotesAwaiting = bookings.filter((b) => b.status === 'quote_requested').length;
  const pendingMods = bookings.filter((b) => b.status === 'modification_pending').length;
  const confirmedUpcoming = bookings.filter((b) => b.status === 'confirmed' && b.pickupDate >= today).length;
  const completedThisMonth = bookings.filter((b) => {
    if (b.status !== 'completed') return false;
    const d = new Date(b.pickupDate);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  const recent = [...bookings]
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
    .slice(0, 6);

  const Stat = ({ icon: Icon, label, value, tone, onClick, testId }) => (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white rounded-xl border border-gray-200 p-5 hover:border-black transition-colors w-full"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <ChevronRight className="h-4 w-4 text-gray-400" />
      </div>
      <p className="text-3xl font-bold text-black">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </button>
  );

  return (
    <AdminLayout
      title="Overview"
      subtitle="Today's pulse across bookings, quotes and operations"
      actions={
        <div className="flex gap-2">
          <Button onClick={() => navigate('/admin/dispatch')} className="bg-black text-white hover:bg-gray-800" data-testid="new-booking-btn">
            <Send className="h-4 w-4 mr-2" />
            New Booking
          </Button>
          <Button onClick={() => navigate('/admin/bookings')} variant="outline" className="border-gray-300" data-testid="view-all-bookings-btn">
            View All Bookings
          </Button>
        </div>
      }
    >
      {loading ? (
        <p className="text-sm text-gray-600">Loading…</p>
      ) : (
        <div className="space-y-6">
          {/* Stat grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat
              icon={Calendar}
              label="Pick-ups Today"
              value={todayBookings.length}
              tone="bg-black/5 text-black"
              onClick={() => navigate('/admin/bookings')}
              testId="stat-today"
            />
            <Stat
              icon={Send}
              label="Quotes Awaiting Action"
              value={quotesAwaiting}
              tone="bg-amber-100 text-amber-700"
              onClick={() => navigate('/admin/bookings')}
              testId="stat-quotes"
            />
            <Stat
              icon={AlertCircle}
              label="Modifications Pending"
              value={pendingMods}
              tone="bg-orange-100 text-orange-700"
              onClick={() => navigate('/admin/bookings')}
              testId="stat-mods"
            />
            <Stat
              icon={CheckCircle2}
              label="Confirmed Upcoming"
              value={confirmedUpcoming}
              tone="bg-emerald-100 text-emerald-700"
              onClick={() => navigate('/admin/bookings')}
              testId="stat-confirmed"
            />
          </div>

          {/* Today's bookings */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-black">Today&rsquo;s Pick-ups</h2>
                <p className="text-xs text-gray-500">{todayBookings.length} scheduled for {today}</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/admin/bookings')} className="border-gray-300">
                See All
              </Button>
            </div>

            {todayBookings.length === 0 ? (
              <p className="text-sm text-gray-500">No pick-ups scheduled for today.</p>
            ) : (
              <div className="divide-y divide-gray-100" data-testid="today-list">
                {todayBookings
                  .sort((a, b) => (a.pickupTime || '').localeCompare(b.pickupTime || ''))
                  .map((b) => {
                    const meta = STATUS_META[b.status] || STATUS_META.confirmed;
                    return (
                      <div key={b.id} className="flex items-center justify-between py-3 gap-4">
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="text-center shrink-0 w-16">
                            <p className="text-lg font-bold text-black">{b.pickupTime}</p>
                            <p className="text-[10px] uppercase text-gray-500 tracking-wider">{b.serviceType?.split(' ')[0]}</p>
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-black truncate">
                              {b.firstName} {b.lastName}
                            </p>
                            <p className="text-xs text-gray-600 truncate">
                              {b.pickupLocation} &rarr; {b.dropoffLocation}
                            </p>
                          </div>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${meta.tone}`}>
                          {meta.label}
                        </span>
                      </div>
                    );
                  })}
              </div>
            )}
          </Card>

          {/* Recent bookings */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-black">Recent Activity</h2>
                <p className="text-xs text-gray-500">Last 6 bookings created</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <TrendingUp className="h-4 w-4" />
                {completedThisMonth} completed this month
              </div>
            </div>

            {recent.length === 0 ? (
              <p className="text-sm text-gray-500">No bookings yet.</p>
            ) : (
              <div className="divide-y divide-gray-100" data-testid="recent-list">
                {recent.map((b) => {
                  const meta = STATUS_META[b.status] || STATUS_META.confirmed;
                  return (
                    <div key={b.id} className="flex items-center justify-between py-3 gap-4">
                      <div className="min-w-0">
                        <p className="font-semibold text-black truncate">
                          {b.id} &middot; {b.firstName} {b.lastName}
                        </p>
                        <p className="text-xs text-gray-600 truncate flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          {formatUkDateTime(b.pickupDate, b.pickupTime)}
                        </p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${meta.tone}`}>
                        {meta.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Danger zone — clear all test data */}
          <Card className="p-5 border border-red-200 bg-red-50/40">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-red-800">Clear all data</p>
                <p className="text-xs text-red-600/80 mt-0.5">Permanently deletes every booking, invoice and customer record. Drivers, your login and the calendar connection are kept. Use this to remove test data before going live.</p>
              </div>
              <Button
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 shrink-0"
                onClick={resetData}
                disabled={resetting}
                data-testid="admin-reset-data-btn"
              >
                {resetting ? 'Clearing…' : 'Clear all data'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOverview;
